// @file Fetch initial state for a whiteboard wall.
import { captureFetchException } from '@@/bits/error_tracker'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useWhiteboardCardTemplatesStore } from '@@/pinia/whiteboard_card_templates'
import { useWhiteboardCollaborationStore } from '@@/pinia/whiteboard_collaboration'
import { useWhiteboardFrameBackgroundStore } from '@@/pinia/whiteboard_frame_background'
import { useWhiteboardToolsStore } from '@@/pinia/whiteboard_tools'
import { WallPostPropertiesApi } from '@@/surface/api/wall_post_properties'
import { WhiteboardCollaborationSettingsApi } from '@@/surface/api/whiteboard_collaboration_settings'
import { WhiteboardToolSettingsApi } from '@@/surface/api/whiteboard_tool_settings'
import { useWhiteboardConnectionUri } from '@@/vuecomposables/useWhiteboardConnectionUri'
import { storeToRefs } from 'pinia'

interface InitialStateFetchOptions {
  collaborationSettings?: boolean
  frameBackgrounds?: boolean
  cardTemplates?: boolean
}

export const useWhiteboardFetchInitialState = (): {
  fetchInitialState: (payload?: InitialStateFetchOptions) => void
} => {
  const { isClassroomOrSchoolLibrary, libraryHashid, wallHashid, wallId } = storeToRefs(useSurfaceStore())
  const { setCollaborationSettings } = useWhiteboardCollaborationStore()
  const { setToolSettings } = useWhiteboardToolsStore()
  const { fetchAndSetAttachmentSettingPermissions, setWallPostProperties } = useSurfacePostPropertiesStore()
  const whiteboardFrameBackgroundStore = useWhiteboardFrameBackgroundStore()
  const whiteboardCardTemplatesStore = useWhiteboardCardTemplatesStore()

  // #region Define
  const fetchCollaborationSettingsAndSaveToStore = async (): Promise<void> => {
    try {
      const settings = await WhiteboardCollaborationSettingsApi.fetch(wallHashid.value)
      setCollaborationSettings(settings)
    } catch (e) {
      captureFetchException(e, { source: 'fetchCollaborationSettingsAndSaveToStore' })
    }
  }

  const fetchToolSettingsAndSaveToStore = async (): Promise<void> => {
    try {
      // Fetch wall post properties first to ensure attachment_settings is initialized
      // before setToolSettings calls syncToolSettingsToContentPicker.
      // This prevents a race condition where tool settings would be synced to
      // attachment_settings before wallPostProperties is loaded, causing the
      // content picker to show all tools instead of respecting disabled tools.
      const postProperties = await WallPostPropertiesApi.fetchWallPostProperties(wallId.value)
      setWallPostProperties(postProperties)

      const settings = await WhiteboardToolSettingsApi.fetch(wallHashid.value)
      setToolSettings(settings)
    } catch (e) {
      captureFetchException(e, { source: 'fetchToolSettingsAndSaveToStore' })
    }
  }
  // #endregion

  const fetchInitialState = ({
    collaborationSettings = true,
    frameBackgrounds = true,
    cardTemplates = true,
  }: InitialStateFetchOptions = {}): void => {
    // #region Register
    const promises: Array<Promise<void>> = []

    if (collaborationSettings) {
      promises.push(fetchCollaborationSettingsAndSaveToStore())
      promises.push(fetchToolSettingsAndSaveToStore())
    }
    if (frameBackgrounds) {
      promises.push(whiteboardFrameBackgroundStore.fetchFrameBackgroundsAndDefaultFrameBackground())
    }
    if (cardTemplates) {
      promises.push(whiteboardCardTemplatesStore.fetchAndSaveCardTemplatesToStore())
    }

    if (libraryHashid.value !== undefined && isClassroomOrSchoolLibrary.value) {
      promises.push(fetchAndSetAttachmentSettingPermissions(libraryHashid.value))
    }

    promises.push(useWhiteboardConnectionUri().prefetchAccessTokens())
    // #endregion

    // #region Execute
    void Promise.all(promises)
    // #endregion
  }

  return { fetchInitialState }
}
